import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFussstatus } from "../../context/FussstatusContext";
import { useFussstatusGrafik } from "../../context/FussstatusGrafikContext";
import { useKlinischerBefund } from "../../context/KlinischerBefundContext";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import styles from "./Fussstatus.module.css";

const LEFT_KEYS  = ["medLeft",  "latLeft",  "plLeft",  "dorsLeft"];
const RIGHT_KEYS = ["medRight", "latRight", "plRight", "dorsRight"];

// Removes Ulcus grafik findings that are no longer allowed by the current fussstatus.
export function sanitizeUlcusFindings(grafikData, fussData) {
  const allow = (side) => ({
    akut:      fussData[`${side}Ulcus`] === "ja" && (fussData[`${side}UlcusTyp`] || "").includes("akut"),
    abgeheilt: fussData[`${side}Ulcus`] === "ja" && (fussData[`${side}UlcusTyp`] || "").includes("abgeheilt"),
  });
  const linksAllow  = allow("links");
  const rechtsAllow = allow("rechts");

  const filterKeys = (data, keys, allowAkut, allowAbgeheilt) => {
    const result = { ...data };
    keys.forEach((key) => {
      result[key] = (data[key] || []).filter((item) => {
        if (item.befund === "Ulcus akut")      return allowAkut;
        if (item.befund === "Ulcus abgeheilt") return allowAbgeheilt;
        return true;
      });
    });
    return result;
  };

  let result = filterKeys(grafikData, LEFT_KEYS,  linksAllow.akut,  linksAllow.abgeheilt);
  result     = filterKeys(result,     RIGHT_KEYS, rechtsAllow.akut, rechtsAllow.abgeheilt);
  return result;
}

const Fussstatus = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: _data, setData } = useFussstatus();
  const data = {
    ..._data,
    UlcusNotiz: _data.UlcusNotiz ?? "",
  };

  const { setData: setGrafikData } = useFussstatusGrafik();

  // Real-time: sanitize grafik ulcus findings whenever ulcus fields change.
  useEffect(() => {
    setGrafikData((prev) => sanitizeUlcusFindings(prev, data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.linksUlcus, data.linksUlcusTyp, data.rechtsUlcus, data.rechtsUlcusTyp]);
  
  const { data: klinData } = useKlinischerBefund();
  const [errors, setErrors] = useState({});
  const [showWarnSection4, setShowWarnSection4] = useState(false);
  const [showWarnSection5, setShowWarnSection5] = useState(false);
  const [showWarnSection9, setShowWarnSection9] = useState(false);


  const { data: allgemeineData } = useAllgemeineDaten();
  const fussAuswahl = allgemeineData?.fussAuswahl;

  const showLeftFoot = fussAuswahl !== "rechts";
  const showRightFoot = fussAuswahl !== "links";
  const showBothFeet = showLeftFoot && showRightFoot;


useEffect(() => {
  ["links", "rechts"].forEach((side) => {
    const pnp = klinData[`${side}HinweisPNP`];
    if (pnp === "nein" && data[`${side}HinweisCNO`] !== "nein") {
      setData((prev) => ({
        ...prev,
        [`${side}HinweisCNO`]: "nein",
      }));
    }
  });
}, [klinData, data, setData]);


// Force Ulcus = "nein" and block it when BOTH PNP = nein AND PAVK = nein
useEffect(() => {
  ["links", "rechts"].forEach((side) => {
    const pnp  = klinData[`${side}HinweisPNP`];
    const pavk = klinData[`${side}HinweisPAVK`];

    const bothNein = pnp === "nein" && pavk === "nein";

    if (bothNein && data[`${side}Ulcus`] !== "nein") {
      setData((prev) => ({
        ...prev,
        [`${side}Ulcus`]: "nein",
        [`${side}UlcusTyp`]: "",
      }));
    }
  });
}, [klinData, data, setData]);

const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  const SECTION4_FIELDS = ["linksUlcus", "rechtsUlcus", "linksUlcusTyp", "rechtsUlcusTyp"];
  const SECTION5_FIELDS = [
    "linksZustandNachZehenamputation", "rechtsZustandNachZehenamputation",
    "linksZehenamputation",            "rechtsZehenamputation",
  ];
  const SECTION9_FIELDS = ["linksZustandNachAmputation", "rechtsZustandNachAmputation"];

  if (type === "checkbox") {
    if (SECTION5_FIELDS.includes(name)) {
      const existing = data[name] || [];
      if (existing.length > 0 || !checked) setShowWarnSection5(true);
    }
    setData((prev) => {
      const current = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...current, value] };
      } else {
        return { ...prev, [name]: current.filter((v) => v !== value) };
      }
    });
    return;
  }

  if (SECTION4_FIELDS.includes(name) && data[name] !== "") setShowWarnSection4(true);
  if (SECTION5_FIELDS.includes(name) && data[name] !== "") setShowWarnSection5(true);
  if (SECTION9_FIELDS.includes(name) && data[name] !== "") setShowWarnSection9(true);

  setData((prev) => {
    if (name.endsWith("HinweisCNO")) {
      const side = name.startsWith("links") ? "links" : "rechts";
      const pnp = klinData[`${side}HinweisPNP`];
      if (pnp === "nein") return prev;
    }

    const newData = { ...prev, [name]: value };
    const side = name.startsWith("links") ? "links" : "rechts";

    // Zehenamputation "nein" → force ZustandNachAmputation = "nein" + reset checkboxes
    if (name.endsWith("ZustandNachZehenamputation")) {
      if (value === "nein") {
        const ampField = `${side}ZustandNachAmputation`;
        const toesField = `${side}Zehenamputation`;
        
        newData[ampField] = "nein";
        newData[toesField] = [];           // ← This resets the checkboxes
      }
}

    if (name.endsWith("HinweisPNP") && value === "nein") {
      const cnoField = `${side}HinweisCNO`;
      newData[cnoField] = "nein";
    }

    if (name === "linksUlcus" && value === "nein") newData.linksUlcusTyp = "";
    if (name === "rechtsUlcus" && value === "nein") newData.rechtsUlcusTyp = "";
    if (name === "linksHinweisCNO" && value === "nein") newData.linksHinweisCNOTyp = "";
    if (name === "rechtsHinweisCNO" && value === "nein") newData.rechtsHinweisCNOTyp = "";

    return newData;
  });

  // Clear errors
  if (name.endsWith("Ulcus") && value !== "ja") {
    const side = name.startsWith("links") ? "links" : "rechts";
    setErrors((prev) => ({ ...prev, [`${side}UlcusTyp`]: "" }));
  }
  if (name.endsWith("HinweisCNO") && value !== "ja") {
    const side = name.startsWith("links") ? "links" : "rechts";
    setErrors((prev) => ({ ...prev, [`${side}HinweisCNOTyp`]: "" }));
  }
};


const validateForm = () => {
  const newErrors = {};

    // Validate dialysepflichtigeNiereninsuffienz (no left/right)
  if (!data.dialysepflichtigeNiereninsuffienz) {
    newErrors.dialysepflichtigeNiereninsuffienz = "Bitte wählen Sie eine Option";
  }
  
  // Only validate LEFT foot fields when shown
  if (showLeftFoot) {
    const leftFields = [
      "linksDrohendeDorsaleUlcera",
      "linksPraeulzerativeVeranderungen",
      "linksUlcus",
      "linksZustandNachZehenamputation",
      "linksFussdeformitaeten",
      "linksDorsalextensionOSG",
      "linksExtensionGrosszehengrundgelenk",
      "linksHinweisCNO",
      "linksZustandNachAmputation",
      "linksPedographischerBefund",
    ];
    leftFields.forEach((field) => {
      if (!data[field]) {
        newErrors[field] = "Bitte wählen Sie eine Option";
      }
    });
  }
  
  // Only validate RIGHT foot fields when shown
  if (showRightFoot) {
    const rightFields = [
      "rechtsDrohendeDorsaleUlcera",
      "rechtsPraeulzerativeVeranderungen",
      "rechtsUlcus",
      "rechtsZustandNachZehenamputation",
      "rechtsFussdeformitaeten",
      "rechtsDorsalextensionOSG",
      "rechtsExtensionGrosszehengrundgelenk",
      "rechtsHinweisCNO",
      "rechtsZustandNachAmputation",
      "rechtsPedographischerBefund",
    ];
    rightFields.forEach((field) => {
      if (!data[field]) {
        newErrors[field] = "Bitte wählen Sie eine Option";
      }
    });
  }

  // UlcusTyp and CNOTyp validation
  ["links", "rechts"].forEach((side) => {
    if (showLeftFoot || showRightFoot) {  // Only validate if foot is shown
      if (
        data[`${side}Ulcus`] === "ja" &&
        (!data[`${side}UlcusTyp`] || data[`${side}UlcusTyp`].trim() === "")
      ) {
        newErrors[`${side}UlcusTyp`] = "Bitte mindestens eine Option wählen (abgeheilt und/oder akut)";
      }
      if (data[`${side}HinweisCNO`] === "ja" && !data[`${side}HinweisCNOTyp`]) {
        newErrors[`${side}HinweisCNOTyp`] = "Bitte wählen Sie den Typ";
      }
    }
  });

    // Zustand nach Zehenamputation validation
  ["links", "rechts"].forEach((side) => {
    if ((side === "links" && !showLeftFoot) || (side === "rechts" && !showRightFoot)) {
      return;
    }

    const radioField = `${side}ZustandNachZehenamputation`;
    const checkboxField = `${side}Zehenamputation`;

    if (data[radioField] === "ja") {
      const selectedToes = data[checkboxField] || [];
      if (selectedToes.length === 0) {
        newErrors[checkboxField] = "Bitte wählen Sie mindestens eine Zehe aus";
      }
    }
  });

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      navigate("/fussstatusGrafik");
    }
  };

  const renderRadioGroup = (name, options, sideTitle = null, groupHint = null) => (
    <div className={styles["col-sm-4"]}>
      {sideTitle && (
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>{sideTitle}</h3>
      )}
      <div className={styles["radio-group"]}>
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const perOptionHint = typeof option === "string" ? null : option.hint;
          return (
            <label key={value} className={styles["radio-inline"]}>
              <input
                type="radio"
                name={name}
                value={value}
                checked={data[name] === value}
                onChange={handleChange}
              />
              {value}
              {perOptionHint && (
                <span className={styles["small-hint"]}> ({perOptionHint})</span>
              )}
              {!perOptionHint && value === "ja" && groupHint && (
                <span className={styles["small-hint"]}>{groupHint}</span>
              )}
            </label>
          );
        })}
      </div>
      {errors[name] && <p className={styles["error-text"]}>{errors[name]}</p>}
    </div>
  );

const renderUlcusColumn = (side) => {
  const field = `${side}Ulcus`;
  const typField = `${side}UlcusTyp`;
  // Helper: current values as Set for easy .has() check
  const currentValues = new Set(
    data[typField]
      ? data[typField].split('/').map(s => s.trim()).filter(Boolean)
      : []
  );
  const handleUlcusTypeChange = (e) => {
    const { value, checked } = e.target;
    const newSet = new Set(currentValues);
    if (checked) {
      newSet.add(value);
    } else {
      newSet.delete(value);
    }
    // Sort so "abgeheilt" always comes before "akut" → consistent string
    const newValue = Array.from(newSet).sort().join('/');
    if (currentValues.size > 0 || !checked) setShowWarnSection4(true);
    setData((prev) => ({
      ...prev,
      [typField]: newValue
    }));
    // Clear error when user makes a selection
    if (newValue !== '') {
      setErrors((prev) => ({ ...prev, [typField]: '' }));
    }
  };

  // condition moved out + class added to group
  const mustBeNeinAndBlocked = 
    klinData[`${side}HinweisPNP`] === "nein" && 
    klinData[`${side}HinweisPAVK`] === "nein";

  return (
    <div className={styles["col-sm-4"]}>
      <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>
        {side === "links" ? "Linker Fuß" : "Rechter Fuß"}
      </h3>

      {/* Changed: added disabled-group class to the whole radio container when blocked */}
      <div 
        className={`${styles["radio-group"]} ${mustBeNeinAndBlocked ? styles["disabled-group"] : ""}`}
      >
        {["ja", "nein"].map((v) => {
          const isDisabled = mustBeNeinAndBlocked && v === "ja";
          return (
            <label
              key={v}
              className={styles["radio-inline"]}
              style={isDisabled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            >
              <input
                type="radio"
                name={field}
                value={v}
                checked={data[field] === v}
                onChange={handleChange}
                disabled={isDisabled}
              />
              {v}
            </label>
          );
        })}
      </div>

      {data[field] === "ja" && (
        <div className={styles["conditional-wrapper"]}>
          <div className={styles["checkbox-group"]}>
            {["abgeheilt", "akut"].map((v) => (
              <label key={v} className={styles["checkbox-inline"]}>
                <input
                  type="checkbox"
                  value={v}
                  checked={currentValues.has(v)}
                  onChange={handleUlcusTypeChange}
                />
                {v}
              </label>
            ))}
          </div>
        </div>
      )}
      {errors[field] && <p className={styles["error-text"]}>{errors[field]}</p>}
      {errors[typField] && <p className={styles["error-text"]}>{errors[typField]}</p>}
    </div>
  );
};

  const renderCNOColumn = (side) => {
    const statusField = `${side}HinweisCNO`;
    const typField = `${side}HinweisCNOTyp`;

    return (
      <div className={styles["col-sm-4"]}>
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>
          {side === "links" ? "Linker Fuß" : "Rechter Fuß"}
        </h3>

        <div className={styles["radio-group"]}>
          {["ja", "nein"].map((v) => (
            <label key={v} className={styles["radio-inline"]}>
              <input
                type="radio"
                name={statusField}
                value={v}
                checked={data[statusField] === v}
                onChange={handleChange}
              />
              {v}
            </label>
          ))}
        </div>

        {data[statusField] === "ja" && (
          <div className={styles["conditional-wrapper"]}>
            <div className={styles["radio-group"]}>
              {["inaktiv", "floride/aktiv"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name={typField}
                    value={v}
                    checked={data[typField] === v}
                    onChange={handleChange}
                  />
                  {v}
                </label>
              ))}
            </div>
          </div>
        )}

        {errors[statusField] && (
          <p className={styles["error-text"]}>{errors[statusField]}</p>
        )}
        {errors[typField] && (
          <p className={styles["error-text"]}>{errors[typField]}</p>
        )}
      </div>
    );
  };

  const renderFussdeformitaetenGroup = (side) => {
    const name = `${side}Fussdeformitaeten`;
    const optionsTop = ["ja", "nein"];
    const optionBottom = "hochgradig";

    return (
      <div className={styles["col-sm-4"]}>
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>
          {side === "links" ? "Linker Fuß" : "Rechter Fuß"}
        </h3>

        <div className={styles["radio-group"]}>
          {optionsTop.map((v) => (
            <label key={v} className={styles["radio-inline"]}>
              <input
                type="radio"
                name={name}
                value={v}
                checked={data[name] === v}
                onChange={handleChange}
              />
              {v}
            </label>
          ))}
        </div>

        <div className={styles["radio-group"]} style={{ marginTop: "12px" }}>
          <label className={styles["radio-inline"]}>
            <input
              type="radio"
              name={name}
              value={optionBottom}
              checked={data[name] === optionBottom}
              onChange={handleChange}
            />
            {optionBottom}
          </label>
        </div>

        {errors[name] && <p className={styles["error-text"]}>{errors[name]}</p>}
      </div>
    );
  };

const renderCheckboxRow = () => {
  const selections = data.orthoNeurologischeZusatzbefunde || [];

  const showWarning = selections.length > 0;

  const warningText = showWarning
    ? `Sie sollten aufgrund Ihrer Auswahl (${selections.join(", ")}) Ihre Auswahl erweitern oder Rücksprache mit einer Ärztin / einem Arzt halten.`
    : "";

  return (
    <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
      <div className={styles["col-sm-4"]}>
        <label className={styles.label}>
          Orthopädische / neurologische Zusatzbefunde, Gangabweichung
        </label>
      </div>
      <div className={styles["col-sm-8"]}>
        <div className={styles["radio-group"]}>
          {["Rheuma", "Endoprothetik", "Lähmungen"].map((option) => (
            <label key={option} className={styles["radio-inline"]}>
              <input
                type="checkbox"
                name="orthoNeurologischeZusatzbefunde"
                value={option}
                checked={selections.includes(option)}
                onChange={handleChange}
              />
              {option}
            </label>
          ))}
        </div>

        {showWarning && (
          <div className={styles["ortho-advisory"]}>
            {warningText}
          </div>
        )}

      </div>
    </div>
  );
};

  const renderDisabledAwareRadioGroup = (name, options, sideTitle = null, groupHint = null) => {
  const side = name.startsWith("links") ? "links" : "rechts";
  const zehenField = `${side}ZustandNachZehenamputation`;

  // disabled when Zehenamputation = "nein"
  const isDisabled = data[zehenField] === "nein";

  return (
    <div className={styles["col-sm-4"]}>
      {sideTitle && (
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>{sideTitle}</h3>
      )}
      <div className={`${styles["radio-group"]} ${isDisabled ? styles["disabled-group"] : ""}`}>
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const perOptionHint = typeof option === "string" ? null : option.hint;

          return (
            <label
              key={value}
              className={styles["radio-inline"]}
            >
              <input
                type="radio"
                name={name}
                value={value}
                checked={data[name] === value}
                onChange={handleChange}
                disabled={isDisabled}
              />
              {value}
              {perOptionHint && (
                <span className={styles["small-hint"]}> ({perOptionHint})</span>
              )}
              {!perOptionHint && value === "ja" && groupHint && (
                <span className={styles["small-hint"]}>{groupHint}</span>
              )}
            </label>
          );
        })}
      </div>
      {errors[name] && <p className={styles["error-text"]}>{errors[name]}</p>}
    </div>
  );
};

const renderDisabledAwareCNOColumn = (side) => {
  const statusField = `${side}HinweisCNO`;
  const typField = `${side}HinweisCNOTyp`;
  const pnpField = `${side}HinweisPNP`;
  const isDisabled = klinData[pnpField] === "nein";

  return (
    <div className={styles["col-sm-4"]}>
      <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>
        {side === "links" ? "Linker Fuß" : "Rechter Fuß"}
      </h3>

      {/* Make whole group semi-transparent when disabled */}
      <div className={`${styles["radio-group"]} ${isDisabled ? styles["disabled-group"] : ""}`}>
        {["ja", "nein"].map((v) => (
          <label
            key={v}
            className={styles["radio-inline"]}
          >
            <input
              type="radio"
              name={statusField}
              value={v}
              checked={data[statusField] === v}
              onChange={handleChange}
              disabled={isDisabled} // disable BOTH when PNP=nein
            />
            {v}
          </label>
        ))}
      </div>

      {/* Conditional type selection – disable completely when PNP=nein */}
      {data[statusField] === "ja" && (
        <div className={styles["conditional-wrapper"]}>
          <div className={styles["radio-group"]}>
            {["inaktiv", "floride/aktiv"].map((v) => (
              <label key={v} className={styles["radio-inline"]}>
                <input
                  type="radio"
                  name={typField}
                  value={v}
                  checked={data[typField] === v}
                  onChange={handleChange}
                  disabled={isDisabled} // disable types when forced "nein"
                />
                {v}
              </label>
            ))}
          </div>
        </div>
      )}

      {errors[statusField] && (
        <p className={styles["error-text"]}>{errors[statusField]}</p>
      )}
      {errors[typField] && (
        <p className={styles["error-text"]}>{errors[typField]}</p>
      )}
    </div>
  );
};

const renderZehenamputationColumn = (side) => {
  const radioField = `${side}ZustandNachZehenamputation`;
  const checkboxField = `${side}Zehenamputation`;

  const selections = data[checkboxField] || [];

  const handleToeChange = (e) => {
    const { value, checked } = e.target;
    if (selections.length > 0 || !checked) setShowWarnSection5(true);
    setData((prev) => {
      const current = prev[checkboxField] || [];
      if (checked) {
        return { ...prev, [checkboxField]: [...current, value] };
      } else {
        return { ...prev, [checkboxField]: current.filter((v) => v !== value) };
      }
    });
  };

  return (
    <div className={styles["col-sm-4"]}>
      <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>
        {side === "links" ? "Linker Fuß" : "Rechter Fuß"}
      </h3>

      {/* Ja/Nein Radio */}
      <div className={styles["radio-group"]}>
        {["ja", "nein"].map((v) => (
          <label key={v} className={styles["radio-inline"]}>
            <input
              type="radio"
              name={radioField}
              value={v}
              checked={data[radioField] === v}
              onChange={handleChange}
            />
            {v}
          </label>
        ))}
      </div>
      {errors[radioField] && <p className={styles["error-text"]}>{errors[radioField]}</p>}

      {/* Checkboxes appear directly below */}
      {data[radioField] === "ja" && (
        <div className={styles["conditional-wrapper"]}>
          <label className={styles["toe-section-label"]}>Amputierte Zehen</label>
          <div className={styles["toe-checkbox-group"]}>
            {["D1", "D2", "D3", "D4", "D5"].map((toe) => (
              <label key={toe} className={styles["toe-checkbox-inline"]}>
                <input
                  type="checkbox"
                  value={toe}
                  checked={selections.includes(toe)}
                  onChange={handleToeChange}
                />
                {toe}
              </label>
            ))}
          </div>

          {/* Error message for toe checkboxes */}
          {errors[checkboxField] && (
            <p className={styles["error-text"]}>{errors[checkboxField]}</p>
          )}
        </div>
      )}
    </div>
  );
};

const renderZehenamputationCheckboxes = (side) => {
  const field = `${side}Zehenamputation`;
  const selections = data[field] || [];

  const handleToeChange = (e) => {
    const { value, checked } = e.target;
    setShowWarnSection5(true);
    setData((prev) => {
      const current = prev[field] || [];
      if (checked) {
        return { ...prev, [field]: [...current, value] };
      } else {
        return { ...prev, [field]: current.filter((v) => v !== value) };
      }
    });
  };

  return (
    <div className={styles["col-sm-8"]}>
      <div className={styles["checkbox-group"]}>
        {["D1", "D2", "D3", "D4", "D5"].map((toe) => (
          <label key={toe} className={styles["checkbox-inline"]}>
            <input
              type="checkbox"
              value={toe}
              checked={selections.includes(toe)}
              onChange={handleToeChange}
            />
            {toe}
          </label>
        ))}
      </div>
      {errors[field] && <p className={styles["error-text"]}>{errors[field]}</p>}
    </div>
  );
};

  return (
    <div className={styles["fussstatus-page"]}>
      <h1>Fußstatus</h1>

      <form className={styles["form-horizontal"]} onSubmit={handleSubmit}>
        {/* 1) Dialysepflichtige Niereninsuffienz */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>1) Dialysepflichtige Niereninsuffienz</label>
          </div>
          <div className={styles["col-sm-8"]}>
            <div className={styles["radio-group"]}>
              <label className={styles["radio-inline"]}>
                <input
                  type="radio"
                  name="dialysepflichtigeNiereninsuffienz"
                  value="ja"
                  checked={data.dialysepflichtigeNiereninsuffienz === "ja"}
                  onChange={handleChange}
                />
                ja
              </label>
              <label className={styles["radio-inline"]}>
                <input
                  type="radio"
                  name="dialysepflichtigeNiereninsuffienz"
                  value="nein"
                  checked={data.dialysepflichtigeNiereninsuffienz === "nein"}
                  onChange={handleChange}
                />
                nein
              </label>
            </div>
            {errors.dialysepflichtigeNiereninsuffienz && (
              <p className={styles["error-text"]}>{errors.dialysepflichtigeNiereninsuffienz}</p>
            )}
          </div>
        </div>
        <div className={styles.sectionDivider}></div>

        {/* Titles for desktop */}
        <div className={`${styles.row} ${styles.noMobileGap} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            {/* Empty spacer - matches Q1 */}
          </div>
          {showBothFeet ? (
            // BEIDE: Split into 2 col-sm-4
            <>
              <div className={styles["col-sm-4"]}>
                <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Linker Fuß</h3>
              </div>
              <div className={styles["col-sm-4"]}>
                <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Rechter Fuß</h3>
              </div>
            </>
          ) : (
            // SINGLE: Q1 structure
            <div className={styles["col-sm-8"]}>
              <div style={{ display: 'flex', gap: '40px' }}>
                {showLeftFoot && (
                  <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Linker Fuß</h3>
                )}
                {showRightFoot && (
                  <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Rechter Fuß</h3>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 2) Drohende dorsale Ulcera */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              2) Drohende dorsale Ulcera (Schuhe aus RG II a nicht passend)
            </label>
          </div>
        {showLeftFoot && renderRadioGroup("linksDrohendeDorsaleUlcera", ["ja", "nein"], "Linker Fuß")}
        {showRightFoot && renderRadioGroup("rechtsDrohendeDorsaleUlcera", ["ja", "nein"], "Rechter Fuß")}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* 3) Präulzerative Veränderungen */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>3) Präulzerative Veränderungen</label>
          </div>
        {showLeftFoot && renderRadioGroup("linksPraeulzerativeVeranderungen", ["ja", "nein"], "Linker Fuß")}
        {showRightFoot && renderRadioGroup("rechtsPraeulzerativeVeranderungen", ["ja", "nein"], "Rechter Fuß")}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* 4) Ulcus */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>4) Ulcus</label>
            <p className={styles["ulcus-hint"]}>
              Bei „ja“ muss auf der nächsten Seite (Grafik) zu jeder gewählten Option (abgeheilt und/oder akut) mindestens ein passender Befund angegeben werden.
            </p>
          </div>
        {showLeftFoot && renderUlcusColumn("links")}
        {showRightFoot && renderUlcusColumn("rechts")}
        </div>
        {showWarnSection4 && (
          <div className={styles["amputation-warning"]}>
            <span className={styles["amputation-warning-icon"]}>⚠</span>
            <span>
              Achtung: Sie haben Ulcusdaten geändert. Bitte prüfen Sie die Änderungen.
            </span>
            <button
              type="button"
              className={styles["amputation-warning-close"]}
              onClick={() => setShowWarnSection4(false)}
            >✕</button>
          </div>
        )}

        {/* 4) Ulcus – Notiz (gesamt) */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Ulcus – Notiz</label>
            <p className={styles["ulcus-hint"]}>
              Hier kann eine Notiz für zusätzliche Einzelheiten geschrieben werden.
            </p>
          </div>
          <div className={styles["col-sm-8"]}>
            <textarea
              className={styles["ulcus-note-textarea"]}
              value={data.UlcusNotiz}
              onChange={(e) => setData((prev) => ({
                ...prev,
                UlcusNotiz: e.target.value.slice(0, 200),
              }))}
              placeholder="Ulcus-Notiz"
              maxLength={200}
              rows={4}
            />
            <p>
              Verbleibende Zeichen: {200 - data.UlcusNotiz.length}
            </p>
          </div>
        </div>

        <div className={styles.sectionDivider}></div>

        {/* 5) Zustand nach Zehenamputation */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>5) Zustand nach Zehenamputation</label>
          </div>
          {showLeftFoot && renderZehenamputationColumn("links")}
          {showRightFoot && renderZehenamputationColumn("rechts")}
        </div>
        {showWarnSection5 && (
          <div className={styles["amputation-warning"]}>
            <span className={styles["amputation-warning-icon"]}>⚠</span>
            <span>
              Achtung: Sie haben Zehenamputationsdaten geändert. Bitte prüfen Sie die Änderungen.
            </span>
            <button
              type="button"
              className={styles["amputation-warning-close"]}
              onClick={() => setShowWarnSection5(false)}
            >✕</button>
          </div>
        )}


        <div className={styles.sectionDivider}></div>

        {/* 6) Fußdeformitäten */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>6) Fußdeformitäten (z.{" "}B. Knick-Senkfuß, Hohlfuß, Zehenfehlstellungen) und Dysproportionen, die nicht nach konfektioniertem Leisten zu versorgen sind (z.{" "}B. Spezialschuhe bei DFS)</label>
          </div>
        {showLeftFoot && renderFussdeformitaetenGroup("links")}
        {showRightFoot && renderFussdeformitaetenGroup("rechts")}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* 7) Dorsalextension OSG */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>7) Dorsalextension OSG (oberes Sprunggelenk) eingeschränkt</label>
          </div>
        {showLeftFoot && renderRadioGroup("linksDorsalextensionOSG", ["ja", "nein"], "Linker Fuß", "(<5-10°)")}
        {showRightFoot && renderRadioGroup("rechtsDorsalextensionOSG", ["ja", "nein"], "Rechter Fuß", "(<5-10°)")}
        </div>

        {/* Extension Großzehengrundgelenk */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Extension Großzehengrundgelenk eingeschränkt</label>
          </div>
        {showLeftFoot && renderRadioGroup("linksExtensionGrosszehengrundgelenk", ["ja", "nein"], "Linker Fuß", "(<10°)")}
        {showRightFoot && renderRadioGroup("rechtsExtensionGrosszehengrundgelenk", ["ja", "nein"], "Rechter Fuß", "(<10°)")}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* 8) Hinweis auf CNO */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>8) Hinweis auf CNO (Charcot-Neuro-Osteoarthropathie)</label>
          </div>
        {showLeftFoot && renderDisabledAwareCNOColumn("links")}
        {showRightFoot && renderDisabledAwareCNOColumn("rechts")}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* 9) Zustand nach Amputation */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>9) Zustand nach (mind. transmetatarsaler) Amputation</label>
          </div>
        {showLeftFoot && renderDisabledAwareRadioGroup("linksZustandNachAmputation", ["ja", "nein"], "Linker Fuß")}
        {showRightFoot && renderDisabledAwareRadioGroup("rechtsZustandNachAmputation", ["ja", "nein"], "Rechter Fuß")}
        </div>
        {showWarnSection9 && (
          <div className={styles["amputation-warning"]}>
            <span className={styles["amputation-warning-icon"]}>⚠</span>
            <span>
              Achtung: Sie haben Amputationsdaten geändert. Bitte prüfen Sie die Änderungen.
            </span>
            <button
              type="button"
              className={styles["amputation-warning-close"]}
              onClick={() => setShowWarnSection9(false)}
            >✕</button>
          </div>
        )}
        <div className={styles.sectionDivider}></div>

        {/* Pedographischer Befund (Druckwert) */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Pedographischer Befund (Druckwert)
            </label>
          </div>
          {showLeftFoot && renderRadioGroup(
            "linksPedographischerBefund",
            [
              { value: "unauffällig", hint: "bis 350 kPa" },
              { value: "auffällig", hint: "bis 500 kPa" },
              { value: "extreme Druckspritzen", hint: "ab 500 kPa" },
            ],
            "Linker Fuß"
          )}
          {showRightFoot && renderRadioGroup(
            "rechtsPedographischerBefund",
            [
              { value: "unauffällig", hint: "bis 350 kPa" },
              { value: "auffällig", hint: "bis 500 kPa" },
              { value: "extreme Druckspritzen", hint: "ab 500 kPa" },
            ],
            "Rechter Fuß"
          )}
        </div>
        <div className={styles.sectionDivider}></div>

        {/* Checkbox row */}
        {renderCheckboxRow()}

        {/* Actions */}
        <div className={`${styles.row} ${styles.actions}`}>
          <button
            type="button"
            className={styles["back-btn"]}
            onClick={() => navigate("/klinischerBefund")}
          >
            Zurück
          </button>
          <input type="submit" value="Weiter" className={styles["btn-primary"]} />
        </div>
      </form>
    </div>
  );
};

export default Fussstatus;